import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { listApprovedBookers, listDriverCarrierLinks, linkDriverToCarrier, unlinkDriverFromCarrier } from "@/lib/api";
import { driverName } from "./DriverList";

/** Staff view of one driver: contact details + which carrier companies they drive for. */
export function DriverRecordSheet({ driver, clientId, onOpenChange }) {
  const [links, setLinks] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!driver) return;
    const { data } = await listDriverCarrierLinks(driver.id);
    setLinks(data || []);
  }, [driver]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    if (!clientId) return;
    listApprovedBookers({ clientId }).then(({ data }) => setCarriers(data || []));
  }, [clientId]);

  const run = async (fn, ok) => {
    setBusy(true);
    const { error } = await fn();
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success(ok);
    setPick("");
    reload();
  };

  const linked = new Set(links.map((l) => l.approved_booker_id));
  const available = carriers.filter((c) => !linked.has(c.id));

  return (
    <Sheet open={!!driver} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md" data-testid="driver-record-sheet">
        {driver && (
          <>
            <SheetHeader>
              <SheetTitle data-testid="driver-record-name">{driverName(driver)}</SheetTitle>
              <SheetDescription>{[driver.email, driver.phone].filter(Boolean).join(" · ")}</SheetDescription>
            </SheetHeader>
            <div className="mt-6 space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Drives for</div>
              {links.length === 0 && <p className="text-sm text-slate-500" data-testid="driver-carriers-empty">Not linked to any carrier yet.</p>}
              <div className="flex flex-wrap gap-2">
                {links.map((l) => (
                  <Badge key={l.approved_booker_id} variant="secondary" className="gap-1 pr-1" data-testid={`driver-carrier-${l.approved_booker_id}`}>
                    {l.carrier?.name || "Carrier"}
                    {l.via && <span className="text-[10px] text-slate-500">(via bookings)</span>}
                    <button type="button" disabled={busy} className="rounded p-0.5 hover:bg-slate-300"
                            onClick={() => run(() => unlinkDriverFromCarrier(l.approved_booker_id, driver.id), "Removed from carrier.")}
                            data-testid={`driver-carrier-remove-${l.approved_booker_id}`} aria-label="Remove">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2 pt-2">
                <Select value={pick} onValueChange={setPick}>
                  <SelectTrigger className="flex-1" data-testid="driver-carrier-select"><SelectValue placeholder="Add to a carrier…" /></SelectTrigger>
                  <SelectContent>
                    {available.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`driver-carrier-option-${c.id}`}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button disabled={!pick || busy} onClick={() => run(() => linkDriverToCarrier(pick, driver.id), "Added to carrier.")}
                        data-testid="driver-carrier-add">Add</Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
